import { useFragment, UseFragmentResult } from "@apollo/client";

import { client } from "@/client/apollo";
import {
  AnswerBlockFragment,
  AnswerBlockFragmentDoc,
  AssessmentFragment,
  AssessmentFragmentDoc,
  BlockSnapshotFragment,
  InstitutionFragment,
  InstitutionFragmentDoc,
  QuestionFragment,
  QuestionFragmentDoc,
  SessionFragmentDoc,
  WorkAnswerBlocksFragment,
  WorkAnswerBlocksFragmentDoc,
  WorkSubmitAttemptFragment,
  WorkSubmitAttemptFragmentDoc,
} from "@/generated/graphql";

/**
 * Select a Question from the cache.
 */
export function useQuestion(questionId: string) {
  return useFragment<QuestionFragment>({
    from: {
      __typename: "Question",
      id: questionId,
    },
    fragment: QuestionFragmentDoc,
    fragmentName: "Question",
  });
}

/** List latest AnswerBlocks from the apollo cache. */
export function useAnswerBlocks(workId: string): AnswerBlockFragment[] {
  const { data, complete } = useFragment<WorkAnswerBlocksFragment>({
    from: {
      __typename: "Work",
      id: workId,
    },
    fragment: WorkAnswerBlocksFragmentDoc,
    fragmentName: "WorkAnswerBlocks",
  });

  return complete ? data.answerBlocks : [];
}

/** Read a single Answer Block fragment. */
export function useAnswerBlock(
  answerBlockId: string | null
): UseFragmentResult<AnswerBlockFragment> {
  return useFragment<AnswerBlockFragment>({
    from:
      answerBlockId == null
        ? null
        : {
            id: answerBlockId,
            __typename: "AnswerBlock",
          },
    fragment: AnswerBlockFragmentDoc,
    fragmentName: "AnswerBlock",
  });
}

/**
 * Select the singleton Institution data.
 */
export function useInstitution() {
  return useFragment<InstitutionFragment>({
    from: `Institution:{}`,
    fragment: InstitutionFragmentDoc,
    fragmentName: "Institution",
  });
}

/**
 * Read the loaded Assessment.
 */
export function useAssessmentFragment(): UseFragmentResult<AssessmentFragment> {
  return useFragment<AssessmentFragment>({
    from: `Assessment:{}`,
    fragment: AssessmentFragmentDoc,
    fragmentName: "Assessment",
  });
}

/**
 * Select Work's final and draft submission metadata from the the cache.
 */
export function useWorkSubmissionMetadata(workId: string) {
  return useFragment<WorkSubmitAttemptFragment>({
    from: {
      __typename: "Work",
      id: workId,
    },
    fragment: WorkSubmitAttemptFragmentDoc,
    fragmentName: "WorkSubmitAttempt",
  });
}

/**
 * List all AnswerBlocks but with their latest snapshot replaced with the
 * matching snapshot from `snapshots`.
 *
 * If an AnswerBlock does not have a matching snapshot, its `latestSnapshot` is
 * set to null.
 *
 * Useful to match submitted Block Snapshots with the current list of Answer
 * Blocks loaded in cache.
 */
export function useAnswerBlocksWithSnapshots(
  workId: string,
  snapshots: BlockSnapshotFragment[]
): AnswerBlockFragment[] {
  const answerBlocks = useAnswerBlocks(workId);

  return answerBlocks.map((block) => {
    const snapshot = snapshots.find(
      (snapshot) => snapshot.answerBlockId === block.id
    );
    return { ...block, latestBlockSnapshot: snapshot ?? null };
  });
}
/**
 * Verification of the session.
 */
export const useSessionVerification = (assessmentId: string): boolean => {
  const data = client.readFragment({
    id: "Session:{}",
    fragment: SessionFragmentDoc,
    fragmentName: "Session",
    variables: {
      assessmentId: assessmentId,
    },
  });

  return data?.verified ?? false;
};
